import React, { CSSProperties, useEffect, useState } from 'react';
import {
    DndContext, KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    DragEndEvent,
    UniqueIdentifier,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    useReactTable,
    ColumnDef,
    getCoreRowModel,
    Row,
    flexRender,
} from '@tanstack/react-table';
import { RiDragMove2Fill } from "react-icons/ri";
import Container from '../../../constants/Container';
import AppURL from '../../../constants/AppURL';
import useFetchSubData from '../../../helpers/useSubFetch';
import SelectInputBox from '../../../constants/SelectInputBox';
import { ToleOptions } from '../../../constants/Options';
import { patchDataToTheServer } from '../../../helpers/api';
import { ToastSuccess } from '../../../constants/Toast';


export interface Customer {
    member_sn: number;
    mem_name: string;
    tap_size_name: string;
    province_name: string;
    municipality_name: string;
    district_name: string;
    ctiz_issue_district_name: string;
    tole_name: string;
    add_nep: string;
    add_eng: string;
    ward_no: number;
    sms_mob_no: string;
    phone_no: string;
    spouse_name: string;
    meter_sn: string;
    sadhiyar_no: number;
}

// Draggable Row Component
const DraggableRow = ({ row }: { row: Row<Customer> }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: row.id, // ID from React Table
    });
    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: 'relative',
        zIndex: transform ? 1 : 0,
    };

    return (
        <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {row.getVisibleCells().map((cell) => (
                <td className='border text-[12px] text-gray-700 text-nowrap py-1 px-2' key={cell.id} style={{ width: cell.column.getSize() }}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
            ))}
        </tr>
    );
};

// Main Table Component
const ToleWiseUserArrangeCreateListView = () => {
    const [onSelectMarga, setOnSelectMarga] = useState(1);
    const { data: marga_customer, loading, error, refetch } = useFetchSubData(AppURL.FrontOfficeURL + `front/customer/sadhiyar/no/wise/list/view/?marga_tole=${onSelectMarga}`, onSelectMarga);

    const [data, setData] = useState<Customer[]>([]); // Initialize with an empty array
    const toleOptions = ToleOptions(marga_customer?.marga_tole);
    useEffect(() => {
        if (marga_customer?.consumer) {
            setData(marga_customer.consumer); // Update `data` when `marga_customer` is fetched
        }
    }, [marga_customer]);

    const dataIds = React.useMemo<UniqueIdentifier[]>(
        () => data?.map((item: Customer) => item.member_sn?.toString()), // Ensure consistent ID format
        [data]
    );

    const columns = React.useMemo<ColumnDef<Customer>[]>(
        () => [
            {
                id: 'drag-handle',
                header: 'Move',
                cell: ({ row }) => <button className='flex justify-center w-full'><RiDragMove2Fill /></button>, // Drag handle placeholder
                size: 60,
            },
            { accessorKey: 'member_sn', header: 'Member SN' },
            { accessorKey: 'mem_name', header: 'Member Name' },
            { accessorKey: 'add_eng', header: 'Address' },
            { accessorKey: 'ward_no', header: 'Ward No.' },
            { accessorKey: 'sms_mob_no', header: 'Phone No.' },
            { accessorKey: 'spouse_name', header: 'Spouse name' },
            { accessorKey: 'municipality_name', header: 'Municipality name' },
            { accessorKey: 'tap_size_name', header: 'Tap Size' },
            { accessorKey: 'meter_conn_date', header: 'Meter Con.' },
            { accessorKey: 'serial_suffix', header: 'Serial Suffix' },
            { accessorKey: 'meter_sn', header: 'Meter Serial No.' },
            { accessorKey: 'sadhiyar_no', header: 'Sadhiyar No.' },
        ],
        []
    );

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.member_sn.toString(),
    });

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            setData((prev: Customer[]) => {
                const oldIndex = prev.findIndex((item) => item.member_sn.toString() === active.id);
                const newIndex = prev.findIndex((item) => item.member_sn.toString() === over.id);

                if (oldIndex === -1 || newIndex === -1) {
                    console.error('Error: Invalid indices for drag operation.');
                    return prev;
                }

                const updatedData = arrayMove(prev, oldIndex, newIndex);
                console.log('Reordered member_sn:', updatedData.map((item) => item.member_sn));

                // Send reordered data to the backend
                const reorderedMemberSn = updatedData.map((item) => item.member_sn);
                sendReorderedData(reorderedMemberSn);

                return updatedData;
            });
        }
    };

    const sendReorderedData = async (reorderedData: number[]) => {
        try {
            // Send the request and wait for the response
            const response = await patchDataToTheServer(
                AppURL.FrontOfficeURL + "front/customer/sadhiyar/no/update/view/",
                { reordered_member_sn: reorderedData }
            );

            // Log the response status
            if (response && response.status) {
                ToastSuccess({ message: response.message });
            } else {
                console.error('No status returned in the response.');
            }

            // Handle the server response as needed
            console.log('Server response:', response);
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor)
    );
    return (
        <Container padding>
            <div className='pb-3 w-1/4'>
                <div className='w-full'>
                    <SelectInputBox
                        options={toleOptions}
                        optionSelect={(name, selectedOption) => {
                            setOnSelectMarga(selectedOption?.value)
                        }}
                        isLoading={loading}
                        value={onSelectMarga}
                        required={false}
                        placeholder='Select province'
                        name='province'
                    />
                </div>
            </div>
            <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
                sensors={sensors}
            >
                <div className="">
                    <table className='w-full'>
                        <thead className='bg-green-300'>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th className='border py-1.5 px-2 font-semibold text-[13px]' key={header.id} colSpan={header.colSpan}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(header.column.columnDef.header, header.getContext())}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        {
                            loading ? <>Loading....</> :
                                <tbody>
                                    <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                                        {table.getRowModel().rows.map((row, index) => (
                                            <DraggableRow key={`${index}`} row={row} />
                                        ))}
                                    </SortableContext>

                                </tbody>}
                    </table>
                </div>
            </DndContext>
        </Container>
    );
};

export default ToleWiseUserArrangeCreateListView;
